import { ActionReducer } from '@ngrx/store';
import {
  equals,
  isNil,
  mergeAll,
  not,
  omit,
  pick,
} from '@qld-recreational/ramda';
import { loadEnvs } from '../settings/settings.actions';
import { StorageService } from '@qld-recreational/storage';

const STORAGE_KEY = 'IONIC_STORAGE_APP_STATE';

export const fetchState = async (storage: StorageService): Promise<{}> => {
  return await storage.fetchState();
};

export const saveState = async (
  storage: StorageService,
  prevState: Record<string, Record<string, unknown>> | null,
  nextState: Record<string, Record<string, unknown>>,
  keys: (string | Record<string, string[]>)[],
): Promise<void> => {
  const tasks = keys.flatMap((key) => {
    if (typeof key === 'string') {
      const prev = prevState?.[key];
      const next = nextState[key];
      return equals(prev, next)
        ? []
        : [storage.set(`${STORAGE_KEY}-${key}`, next)];
    }

    return Object.entries(key).flatMap(([outerKey, fields]) => {
      const prevSlice = pick(fields, prevState?.[outerKey] ?? {});
      const nextSlice = pick(fields, nextState[outerKey] ?? {});
      return equals(prevSlice, nextSlice)
        ? []
        : [storage.set(`${STORAGE_KEY}-${outerKey}`, nextSlice)];
    });
  });

  await Promise.allSettled(tasks);
};

export const StorageSyncActions = {
  HYDRATED: 'NSIS_APP_HYDRATED',
};

export const NGRX_ACTIONS = {
  INIT: '@ngrx/store/init',
  UPDATE_REDUCERS: '@ngrx/store/update-reducers',
  RECOMPUTE: '@ngrx/store-devtools/recompute',
  EFFECTS_INIT: '@ngrx/effects/init',
};

export const NGRX_STORAGE_SYNC_IGNORE_ACTIONS = [
  StorageSyncActions.HYDRATED,
  NGRX_ACTIONS.INIT,
  NGRX_ACTIONS.EFFECTS_INIT,
  NGRX_ACTIONS.UPDATE_REDUCERS,
  NGRX_ACTIONS.RECOMPUTE,
  loadEnvs.type,
];

export interface StorageSyncOptions {
  hydratedStateKey?: string;
}

export const storageSync = (options?: StorageSyncOptions) => {
  const { hydratedStateKey } = Object.assign({}, options || {});

  let hydratedState;

  return (reducer: ActionReducer<any>) => (state: any, action: any) => {
    const { type, payload } = action;
    if (equals(type, StorageSyncActions.HYDRATED)) {
      state = Object.assign({}, state, payload);
      hydratedState = state;
    }

    const isHydrated = not(isNil(hydratedState));
    if (
      (equals(type, NGRX_ACTIONS.INIT) ||
        equals(type, NGRX_ACTIONS.UPDATE_REDUCERS)) &&
      isHydrated
    ) {
      const mergedState = mergeAll([
        pick(action.features, hydratedState),
        omit(action.features, state),
      ]);

      const nextState = reducer(mergedState, action);

      return {
        ...mergedState,
        ...nextState,
      };
    }

    return Object.assign({}, reducer(state, action), {
      [hydratedStateKey]: isHydrated,
    });
  };
};
